// Regla de display de precio con promoción. Mantener en sync con
// describePromoPrice() de apps/frontend/src/store/cartSlice.ts (la tienda).

export interface PromoInput {
  type: string;                  // 'none' | 'bogo' | 'nxm' | 'percentage' | 'fixed' | 'bundle'
  label?: string | null;
  buyQty?: number | null;
  payQty?: number | null;
  discountValue?: number | null;
}

export interface PromoPriceDisplay {
  price: number;                 // precio final por unidad
  originalPrice: number | null;  // precio sin descuento (para tachar)
  percentOff: number | null;
  caption: string | null;        // leyenda para promos por cantidad
}

export const money = (n: number) => `$${Math.round(n).toLocaleString('es-AR')}`;

export function describePromoPrice(price: number, promo: PromoInput | null | undefined): PromoPriceDisplay {
  const base: PromoPriceDisplay = { price, originalPrice: null, percentOff: null, caption: null };
  if (!promo || price <= 0) return base;
  const dv = promo.discountValue ?? 0;

  if (promo.type === 'percentage' || promo.type === 'fixed') {
    const pct = Math.min(100, Math.max(0, dv));
    const final = promo.type === 'percentage'
      ? Math.round(price * (1 - pct / 100))
      : Math.max(0, price - dv);
    if (final < price) {
      return { price: final, originalPrice: price, percentOff: Math.round(((price - final) / price) * 100), caption: null };
    }
  }

  if (promo.type === 'bogo' || promo.type === 'nxm') {
    const buy = promo.buyQty ?? 2;
    const pay = promo.payQty ?? 1;
    if (buy > pay && pay > 0) {
      return { ...base, caption: `${promo.label || `${buy}x${pay}`} · llevando ${buy} pagás ${money((price * pay) / buy)} c/u` };
    }
  }

  if (promo.type === 'bundle' && promo.discountValue != null && promo.discountValue < price) {
    return { ...base, caption: `Llevando ${promo.buyQty ?? 2}: ${money(promo.discountValue)} c/u` };
  }

  return base;
}

interface ApiPromo extends PromoInput {
  isActive: boolean;
  startsAt: number | null;
  endsAt: number | null;
}

/** Primera promo vigente (misma regla que getActivePromo de la tienda). */
export function getActivePromo(promos?: ApiPromo[]): ApiPromo | null {
  if (!promos?.length) return null;
  const now = Math.floor(Date.now() / 1000);
  return promos.find(p =>
    p.isActive && (p.startsAt == null || p.startsAt <= now) && (p.endsAt == null || p.endsAt >= now),
  ) ?? null;
}
